import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import {
  bulkReceiveExpected,
  receiveUnexpected,
  receiveReturnToSender,
} from "@/lib/returns-receiving.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/returns-receiving")({
  component: ReturnsReceivingPage,
});

function ReturnsReceivingPage() {
  const { tenantId, facilityId } = useScope();
  return (
    <>
      <PageHeader
        title="Returns Receiving"
        description="Bulk receive against expected RMAs, capture-then-classify intake, plus fast paths for unexpected receipts and return-to-sender. Inventory motion runs through the existing receive engine; no new inventory path."
      />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/app/returns"><ArrowLeft className="h-4 w-4 mr-1" />Back to returns</Link>
          </Button>
        </div>
        <Tabs defaultValue="bulk">
          <TabsList>
            <TabsTrigger value="bulk">Bulk expected</TabsTrigger>
            <TabsTrigger value="unexpected">Unexpected</TabsTrigger>
            <TabsTrigger value="rts">Return to sender</TabsTrigger>
          </TabsList>
          <TabsContent value="bulk"><BulkPanel tenantId={tenantId} facilityId={facilityId} /></TabsContent>
          <TabsContent value="unexpected"><UnexpectedPanel /></TabsContent>
          <TabsContent value="rts"><RtsPanel /></TabsContent>
        </Tabs>
      </div>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

/* ------------------------- Bulk expected ------------------------- */

function BulkPanel({ tenantId, facilityId }: { tenantId?: string | null; facilityId?: string | null }) {
  const qc = useQueryClient();
  const bulkFn = useServerFn(bulkReceiveExpected);
  const [rmaNumber, setRmaNumber] = useState("");
  const [locationCode, setLocationCode] = useState("");
  const [qtys, setQtys] = useState<Record<string, string>>({});

  const { data: rmaRes } = useQuery({
    queryKey: ["receiving-rma", tenantId, rmaNumber.trim()],
    enabled: !!tenantId && rmaNumber.trim().length > 0,
    queryFn: async () => {
      const { data } = await supabase
        .from("rmas")
        .select("id, rma_number, status, facility_id, customer_id, rma_lines(id, sku_id, qty_expected, qty_received, return_source)")
        .eq("tenant_id", tenantId!)
        .eq("rma_number", rmaNumber.trim())
        .maybeSingle();
      return data;
    },
  });

  const { data: locRes } = useQuery({
    queryKey: ["receiving-loc", tenantId, locationCode.trim()],
    enabled: !!tenantId && locationCode.trim().length > 0,
    queryFn: async () => {
      const { data } = await supabase
        .from("locations")
        .select("id, code, facility_id, active")
        .eq("tenant_id", tenantId!)
        .eq("code", locationCode.trim())
        .maybeSingle();
      return data;
    },
  });

  const lines = (rmaRes?.rma_lines ?? []) as any[];

  const submit = useMutation({
    mutationFn: async () => {
      if (!rmaRes?.id) throw new Error("Scan an RMA first");
      if (!locRes?.id) throw new Error("Scan a destination location");
      if (locRes.facility_id !== rmaRes.facility_id) throw new Error("Location is in another facility");
      const payload = lines
        .map((l: any) => ({ rmaLineId: l.id as string, qty: Math.max(0, Number(qtys[l.id] ?? 0)) }))
        .filter((x) => x.qty > 0);
      if (!payload.length) throw new Error("Enter at least one quantity");
      return bulkFn({ data: { rmaId: rmaRes.id, locationId: locRes.id, lines: payload, deferClassification: true } });
    },
    onSuccess: (r) => {
      const failed = r.results.filter((x) => !x.ok);
      if (failed.length) toast.error(`Received ${r.received}, ${failed.length} failed`);
      else toast.success(`Received ${r.received} line${r.received === 1 ? "" : "s"} — classification deferred`);
      setQtys({});
      qc.invalidateQueries({ queryKey: ["receiving-rma"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader><CardTitle className="text-sm">Bulk Receive Against Expected RMA</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="RMA number">
            <Input value={rmaNumber} onChange={(e) => setRmaNumber(e.target.value)} placeholder="scan or type" autoFocus />
          </Field>
          <Field label="Destination location code">
            <Input value={locationCode} onChange={(e) => setLocationCode(e.target.value)} placeholder="e.g. RCV-01" />
          </Field>
          <div className="flex items-end">
            <Button onClick={() => submit.mutate()} disabled={submit.isPending || !lines.length}>
              Receive selected
            </Button>
          </div>
        </div>
        {rmaRes && (
          <div className="text-xs text-muted-foreground flex gap-3">
            <span>Status: <Badge variant="secondary">{rmaRes.status}</Badge></span>
            <span>Lines: {lines.length}</span>
          </div>
        )}
        {!rmaRes && rmaNumber.trim() && (
          <div className="text-xs text-muted-foreground">No RMA matching that number in this tenant.</div>
        )}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>SKU</TableHead>
                <TableHead className="text-right">Expected</TableHead>
                <TableHead className="text-right">Already received</TableHead>
                <TableHead className="text-right">Remaining</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="w-32 text-right">Receive now</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((l: any) => {
                const remaining = (l.qty_expected ?? 0) - (l.qty_received ?? 0);
                return (
                  <TableRow key={l.id}>
                    <TableCell className="font-mono text-xs">{(l.sku_id as string).slice(0, 8)}</TableCell>
                    <TableCell className="text-right tabular-nums">{l.qty_expected}</TableCell>
                    <TableCell className="text-right tabular-nums">{l.qty_received}</TableCell>
                    <TableCell className="text-right tabular-nums">{remaining}</TableCell>
                    <TableCell className="text-xs">{l.return_source ?? "—"}</TableCell>
                    <TableCell className="text-right">
                      <Input
                        className="text-right tabular-nums"
                        type="number"
                        min={0}
                        max={remaining}
                        value={qtys[l.id] ?? ""}
                        onChange={(e) => setQtys((m) => ({ ...m, [l.id]: e.target.value }))}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {!lines.length && (
                <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">Scan an RMA to see lines.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="text-xs text-muted-foreground">
          Capture-then-classify: units land in inventory now via the existing receive engine; inspection condition stays blank until a reviewer classifies the line on the Disposition page.
        </div>
      </CardContent>
    </Card>
  );
}

/* ----------------------- Fast paths (shared) ---------------------- */

type FastValues = {
  customerId: string;
  skuId: string;
  qty: string;
  locationCode: string;
  note: string;
  orderId?: string;
};

function FastForm({
  title, helper, includeOrder, onSubmit, busy,
}: {
  title: string;
  helper: string;
  includeOrder?: boolean;
  onSubmit: (v: FastValues) => void;
  busy: boolean;
}) {
  const [v, setV] = useState<FastValues>({ customerId: "", skuId: "", qty: "1", locationCode: "", note: "", orderId: "" });
  return (
    <Card>
      <CardHeader><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Customer ID"><Input value={v.customerId} onChange={(e) => setV({ ...v, customerId: e.target.value })} /></Field>
          <Field label="SKU ID"><Input value={v.skuId} onChange={(e) => setV({ ...v, skuId: e.target.value })} /></Field>
          <Field label="Qty"><Input type="number" min={1} value={v.qty} onChange={(e) => setV({ ...v, qty: e.target.value })} /></Field>
          <Field label="Destination location code"><Input value={v.locationCode} onChange={(e) => setV({ ...v, locationCode: e.target.value })} /></Field>
          {includeOrder && <Field label="Original order ID (optional)"><Input value={v.orderId ?? ""} onChange={(e) => setV({ ...v, orderId: e.target.value })} /></Field>}
          <Field label="Note"><Input value={v.note} onChange={(e) => setV({ ...v, note: e.target.value })} /></Field>
        </div>
        <Button onClick={() => onSubmit(v)} disabled={busy}>Receive</Button>
        <div className="text-xs text-muted-foreground">{helper}</div>
      </CardContent>
    </Card>
  );
}

async function resolveLocationId(tenantId: string, code: string): Promise<string> {
  const { data, error } = await supabase
    .from("locations").select("id").eq("tenant_id", tenantId).eq("code", code.trim()).maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error(`Location ${code} not found`);
  return data.id as string;
}

function UnexpectedPanel() {
  const { tenantId, facilityId } = useScope();
  const fn = useServerFn(receiveUnexpected);
  const [busy, setBusy] = useState(false);
  const submit = async (v: FastValues) => {
    if (!tenantId || !facilityId) { toast.error("Pick a facility"); return; }
    setBusy(true);
    try {
      const locationId = await resolveLocationId(tenantId, v.locationCode);
      const r = await fn({ data: {
        tenantId, facilityId,
        customerId: v.customerId.trim(),
        skuId: v.skuId.trim(),
        qty: Math.max(1, Number(v.qty) || 1),
        locationId,
        note: v.note.trim() || undefined,
      } });
      toast.success(`Received as ${r.rmaNumber}`);
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <FastForm
      title="Unexpected receipt"
      helper="Creates a minimal RMA tagged manual_unknown and receives in one step. Inspection + disposition still flow through the Disposition page."
      onSubmit={submit}
      busy={busy}
    />
  );
}

function RtsPanel() {
  const { tenantId, facilityId } = useScope();
  const fn = useServerFn(receiveReturnToSender);
  const [busy, setBusy] = useState(false);
  const submit = async (v: FastValues) => {
    if (!tenantId || !facilityId) { toast.error("Pick a facility"); return; }
    setBusy(true);
    try {
      const locationId = await resolveLocationId(tenantId, v.locationCode);
      const r = await fn({ data: {
        tenantId, facilityId,
        customerId: v.customerId.trim(),
        skuId: v.skuId.trim(),
        qty: Math.max(1, Number(v.qty) || 1),
        locationId,
        originalOrderId: v.orderId?.trim() || undefined,
        note: v.note.trim() || undefined,
      } });
      toast.success(`RTS received as ${r.rmaNumber}`);
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <FastForm
      title="Return to sender / undeliverable"
      helper="Tags return_source = return_to_sender so the seeded RTS fast-track disposition rule (Pass 1) can fire on inspection."
      includeOrder
      onSubmit={submit}
      busy={busy}
    />
  );
}